import { useCallback, useMemo, useState } from 'react'

import { useIntl, FormattedMessage } from 'react-intl'
import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedOfferingListingDetails,
  type OrchestratedAuctionListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { Colors } from '@b-stock/fe-core/design-system'

import { useUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import { CardFooterNote } from '@/buyer-portal/components/layouts/CardLayout/card/CardFooterNote'
import { Card } from '@/buyer-portal/components/layouts/CardLayout/card/ui'
import { FeatureFlags } from '@/helpers/featureFlags'

import type { OpenTrigger } from '../shared/useGalleryTracking'
import { useGalleryTracking } from '../shared/useGalleryTracking'
import { useMergedMedia } from '../shared/useMergedMedia'
import { MediaLightbox } from './MediaLightbox'
import { ThumbnailStrip } from './ThumbnailStrip'
import type { KeyedMedia } from './types'

const MediaCard = styled(Card)`
  background: ${Colors.Semantic.Neutral.light_grey};
  display: flex;
  flex-direction: column;
  grid-area: images;
  width: 100%;
`

export const ListingMediaCard = ({
  listing,
}: {
  listing:
    | OrchestratedOfferingListingDetails
    | OrchestratedAuctionListingDetails
}) => {
  const { formatMessage } = useIntl()
  const isLoggedIn = !!useDecodedAccessToken()?.personId
  const gridAndDownloadEnabled = useFlag(
    FeatureFlags.ENABLE_HP_LDP_MEDIA_GRID_VIEW
  )
  const { media, description } = listing.lot
  const title = listing.lot.title || formatMessage({ id: 'Common.unknown' })

  const { openUnAuthModal } = useUnAuthWarningModal()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const allMedia = useMergedMedia(media, description)

  // Derive keys from content so async Vimeo thumbnail updates don't remount
  // every item (urls change for the resolved video, but videoId is stable;
  // image URLs are stable).
  const items: KeyedMedia[] = useMemo(
    () =>
      allMedia.map((item, index) => ({
        ...item,
        key:
          item.type === 'video'
            ? `vid-${item.videoData.videoId}`
            : `img-${item.urls?.original ?? index}`,
      })),
    [allMedia]
  )

  const hasRealMedia = items.some((item) => !item.isPlaceholder)
  const { trackOpen, trackSignInPrompt } = useGalleryTracking(items)

  const openLightbox = useCallback(
    (index: number, openTrigger: OpenTrigger) => {
      if (!hasRealMedia) return
      if (!isLoggedIn) {
        openUnAuthModal(formatMessage({ id: 'Auction.details.seeMorePhotos' }))
        trackSignInPrompt(index, openTrigger)
        return
      }
      setLightboxIndex(index)
      setLightboxOpen(true)
      trackOpen(index, openTrigger)
    },
    [
      formatMessage,
      hasRealMedia,
      isLoggedIn,
      openUnAuthModal,
      trackOpen,
      trackSignInPrompt,
    ]
  )

  return (
    <MediaCard>
      <ThumbnailStrip items={items} onThumbnailClick={openLightbox} />

      <CardFooterNote>
        <FormattedMessage id="Auction.details.enhancedGallery.representativeSamplesWarning" />
      </CardFooterNote>

      {lightboxOpen && (
        <MediaLightbox
          bulkDownloadEnabled={gridAndDownloadEnabled}
          gridViewEnabled={gridAndDownloadEnabled}
          initialIndex={lightboxIndex}
          items={items}
          onClose={() => setLightboxOpen(false)}
          title={title}
        />
      )}
    </MediaCard>
  )
}
